// src/components/accueil/LigneProbleme.tsx — SCE Issues
// Un problème en une rangée — l'accueil V2 remplace les tuiles (Lot 9).
//
// Une tuile prenait près de 100 px ; la rangée en prend 58 à 64, le titre
// d'abord : c'est lui qu'on cherche. Liseré métier à gauche, comme les
// rangées denses des listes.

import type { ReactNode } from 'react'
import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import { METIERS_COULEURS, METIERS_LABELS_COURT } from '@/config/metiers.config'
import { lienProbleme } from '@/utils/liensFiches'
import type { Issue } from '@/types/issue.types'
import { PastilleNcs } from '@/components/issues/PastilleNcs'

export function LigneProbleme({
  issue,
  meta,
  droite,
  deuxLignes,
}: {
  issue: Issue
  /** Ligne grise sous le titre ; défaut : N° · équipement. */
  meta?: string
  droite?: ReactNode
  /** Titre sur deux lignes (téléphone) plutôt que tronqué à une. */
  deuxLignes?: boolean
}): JSX.Element {
  const ouvrirFiche = useOuvrirFiche()
  return (
    <button
      type="button"
      onClick={() => ouvrirFiche(lienProbleme(issue.id))}
      className="w-full text-left"
      style={{
        display: 'grid',
        gridTemplateColumns: '3px minmax(0, 1fr) auto',
        gap: 14,
        alignItems: 'center',
        minHeight: deuxLignes ? 64 : 58,
        padding: '0 16px 0 0',
        border: 'none',
        borderBottom: '1px solid var(--color-gray-10)',
        background: 'var(--color-bg-card)',
        fontFamily: 'inherit',
        color: 'var(--color-text-primary)',
        cursor: 'pointer',
      }}
    >
      <span
        style={{ alignSelf: 'stretch', backgroundColor: METIERS_COULEURS[issue.metier] }}
        title={METIERS_LABELS_COURT[issue.metier]}
      >
        <span className="sr-only">{METIERS_LABELS_COURT[issue.metier]}</span>
      </span>
      <span className="flex flex-col min-w-0" style={{ gap: 3, padding: '9px 0' }}>
        <span
          style={{
            fontSize: 15,
            fontWeight: 500,
            lineHeight: '19px',
            overflow: 'hidden',
            ...(deuxLignes
              ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }
              : { whiteSpace: 'nowrap', textOverflow: 'ellipsis' }),
          }}
        >
          {issue.titre}
        </span>
        <span
          style={{
            fontSize: 12,
            color: 'var(--color-text-secondary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {/* NC-S déjà envoyée (06/10) : visible dès la file « À qualifier ». */}
          <PastilleNcs issue={issue} />
          {meta ?? [issue.id, issue.numeroSerie].filter(Boolean).join(' · ')}
        </span>
      </span>
      {droite && <span className="flex items-center gap-2" style={{ flexShrink: 0 }}>{droite}</span>}
    </button>
  )
}

/** Âge d'une attente, rouge au-delà du seuil. */
export function PastilleAge({ texte, enRetard }: { texte: string; enRetard: boolean }): JSX.Element {
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: enRetard ? 500 : 400,
        padding: '2px 6px',
        borderRadius: 4,
        whiteSpace: 'nowrap',
        color: enRetard ? 'var(--color-traffic-red)' : 'var(--color-text-secondary)',
        border: enRetard ? '1px solid var(--color-traffic-red)' : '1px solid transparent',
      }}
    >
      {texte}
    </span>
  )
}
